import { createContext, Fragment, useEffect, useReducer, useState } from "react";
import { uMath } from "src/components/Planner/planner/uMath";
import { ts, ts2 } from "src/utils/translate"
import {
  getManufacturerNameToId,
  getSavedDiscounts,
  getServiceNameToId,
  saveDiscount
} from "src/pages/OfferPage/QuotePrices/QuotePricesUtils"
import { GetQuoteResponse } from "src/model/api/quote/get-quote-response.model";
import { getBomRows } from "src/pages/OfferPage/DocuBom/DocuBomUtils";
import { hasPermissionForFuncInProject } from "src/utils/controllerHasPermissionsAdapter";
import { PriceTable } from "src/pages/OfferPage/QuotePrices/PriceTable";
import { PriceRow } from "src/pages/OfferPage/QuotePrices/PriceRow";
import { GrandTotal } from "src/pages/OfferPage/QuotePrices/GrandTotal";
import $ from "jquery";
import { GetAllManufacturerDiscountForProjectResponse } from "src/model/api/manufacturer-discount.model";
import { ProjectQuoteUserProperties } from 'src/model/api/project.model';
import { SetProjectQuotePropertiesRequest, useSetProjectQuotePropertiesMutation } from "src/api/v1/project-ApiSlice";
import { useUpdateProjectManufacturerDiscountMutation } from "src/api/v1/manufacturer-discount-ApiSlice";
import { useUpdateServiceDiscountMutation } from "src/api/v1/quote-ApiSlice";
import { ManufacturerRecommendationResponse } from "src/model/api/manufacturer-recommendation";

const discountPercentReducer = (state: any, action: any) => {
  if (action.type === "update") {
    const newState = Object.assign({}, state);
    newState[action.id] = action.value;
    return newState;
  } else if (action.type === "update_all") {
    const newState = Object.assign({}, state);
    action.ids.forEach((manufacturerId: any) => {
      newState[manufacturerId] = action.value;
    });
    return newState;
  } else if (action.type === "fetch") {
    return action.payload;
  }
};

export interface IPriceContext {
  currency: string,
  onEditHandler: (key: any, value: any) => void,
  onConfirmHandler: (key: any, value: any) => void,
  hasEditPermission: boolean,
}

export const PriceContext = createContext<IPriceContext>({
  currency: "huf",
  onEditHandler: (key: any, value: any) => {},
  onConfirmHandler: (key: any, value: any) => {},
  hasEditPermission: false,
});

export const QuotePrices = (props: {
  projectId: string | undefined;
  tableData: any[];
  quote: GetQuoteResponse;
  allManufacturerDiscountForProject: GetAllManufacturerDiscountForProjectResponse;
  manufacturerRecommendationForProject: ManufacturerRecommendationResponse;
  currency: string;
  quoteUserProperties: ProjectQuoteUserProperties
}) => {
  const {
    projectId,
    tableData,
    quote,
    allManufacturerDiscountForProject,
    manufacturerRecommendationForProject,
    currency,
    quoteUserProperties
  } = props;

  const [ sendTaxValue ] = useSetProjectQuotePropertiesMutation()
  const [ updateManufacturerDiscount ] = useUpdateProjectManufacturerDiscountMutation()
  const [ updateService ] = useUpdateServiceDiscountMutation()

  const [deviceDiscountPercent, setDeviceDiscountPercent] = useState(0);
  const [otherDeviceDiscountPercent, setOtherDeviceDiscountPercent] =
    useState(0);
  const [serviceDiscountPercent, setServiceDiscountPercent] = useState(0);
  const [discountPercents, dispatchDiscountPercents] = useReducer(
    discountPercentReducer,
    null
  );
  const [serverConfirmedDiscounts, setServerConfirmedDiscounts] =
    useState({});
  const [manufacturerNameToId, setManufacturerNameToId] = useState([]);
  const [serviceNameToId, setServiceNameToId] = useState([]);

  const pricesByManufacturers: any = {};
  const pricesByServices: any = {};

  const rows: any = getBomRows(tableData, quoteUserProperties);

  const setAllManufacturerAndDeviceDiscount = (value = 0) => {
    value = uMath.clamp(value, 0, 100);
    const manufacturerIds = Object.values(manufacturerNameToId).filter(
      (x: any) => x in pricesByManufacturers
    );
    manufacturerIds.forEach((manufacturerId) => {
      saveDiscount(
        manufacturerId,
        value,
        projectId,
        serverConfirmedDiscounts,
        setServerConfirmedDiscounts,
        dispatchDiscountPercents,
        updateManufacturerDiscount,
        updateService,
      );
    });
  };

  const setAllServiceDiscount = (value = 0) => {
    value = uMath.clamp(value, 0, 100);
    const serviceIds = Object.values(serviceNameToId);
    serviceIds.forEach((serviceId) => {
      saveDiscount(
        serviceId,
        value,
        projectId,
        serverConfirmedDiscounts,
        setServerConfirmedDiscounts,
        dispatchDiscountPercents,
        updateManufacturerDiscount,
        updateService,
      );
    });
  };

  const onEditHandler = (key: any, value: any) => {
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    if (typeof key === "string" && key === "deviceDiscount") {
      setDeviceDiscountPercent(value);
    } else if (typeof key === "string" && key === "otherDeviceDiscount") {
      setOtherDeviceDiscountPercent(value);
    } else if (typeof key === "string" && key === "serviceDiscount") {
      setServiceDiscountPercent(value);
    } else if (typeof key === "string" && key === "globalTax") {
      sendTaxValue({
        projectId: +projectId!!,
        taxValue: value,
      } as SetProjectQuotePropertiesRequest)
    } else if (typeof key === "string" && key.indexOf("manu_") === 0) {
      const id = key.replace("manu_", "");
      saveDiscount(
        id,
        value,
        projectId,
        serverConfirmedDiscounts,
        setServerConfirmedDiscounts,
        dispatchDiscountPercents,
        updateManufacturerDiscount,
        updateService,
      );
    } else if (typeof key === "string" && key.indexOf("service_") === 0) {
      const id = key.replace("service_", "");
      saveDiscount(
        id,
        value,
        projectId,
        serverConfirmedDiscounts,
        setServerConfirmedDiscounts,
        dispatchDiscountPercents,
        updateManufacturerDiscount,
        updateService,
      );
    }
  };

  const onConfirmHandler = (key: any, value: any) => {
    if (value < 0){
      value = 0;
    }
    if (value > 100){
      value = 100;
    }
    if (typeof key === "string" && key === "deviceDiscount") {
      setAllManufacturerAndDeviceDiscount(value);
    } else if (typeof key === "string" && key === "serviceDiscount") {
      setAllServiceDiscount(value);
    }
  };

  const discountPercentOfManufacturer = (id: string): number => {
    if (id === "__default__") {
      return otherDeviceDiscountPercent;
    }
    if (discountPercents && id in discountPercents) {
      return discountPercents[id];
    } else {
      return 0;
    }
  };

  const discountPercentOfService = (id: string): number => {
    if (discountPercents && id in discountPercents) {
      return discountPercents[id];
    } else {
      return 0;
    }
  };

  useEffect(() => {
    if (quote && manufacturerRecommendationForProject && allManufacturerDiscountForProject) {
      setOtherDeviceDiscountPercent(quote["devices-discount"]);
      getManufacturerNameToId(
          manufacturerRecommendationForProject,
          allManufacturerDiscountForProject,
          setManufacturerNameToId
      );
      getServiceNameToId(setServiceNameToId, quote);
      getSavedDiscounts(
        allManufacturerDiscountForProject,
        quote,
        serverConfirmedDiscounts,
        dispatchDiscountPercents,
        setServerConfirmedDiscounts,
      );
    }
  }, [quote]);

  useEffect(() => {
    if (manufacturerNameToId) {
      $(document).on("create-user-manufacturer.tpc", (e: JQuery.Event<HTMLElement, null>) => {
        const newObj: any = Object.assign({}, manufacturerNameToId);
        newObj[(e.detail as any).manufacturer_name] =
          "umi_" + (e.detail as any).user_manufacturer_id;
        setManufacturerNameToId(newObj);
      });

      $(document).on("update-user-manufacturer.tpc", (e: JQuery.Event<HTMLElement, null>) => {
        const newObj: any = Object.assign({}, manufacturerNameToId);
        if ((e.detail as any).manufacturer_name_old in newObj) {
          newObj[(e.detail as any).manufacturer_name] =
            newObj[(e.detail as any).manufacturer_name_old];
          delete newObj[(e.detail as any).manufacturer_name_old];
        }
        setManufacturerNameToId(newObj);
      });

      $(document).on("delete-user-manufacturer.tpc", (e: JQuery.Event<HTMLElement, null>) => {
        const newObj: any = Object.assign({}, manufacturerNameToId);
        if ((e.detail as any).manufacturer_name in newObj) {
          delete newObj[(e.detail as any).manufacturer_name];
        }
        setManufacturerNameToId(newObj);
      });

      return () => {
        $(document).off("create-user-manufacturer.tpc");
        $(document).off("update-user-manufacturer.tpc");
        $(document).off("delete-user-manufacturer.tpc");
      };
    }
  }, [manufacturerNameToId]);

  const roundToNeededDecimals = (value: number): number =>
    currency === "huf"
      ? Math.round(value)
      : Math.round(value * 100) / 100;

  // First table with 3 rows - devices, services, subtotal
  const devicesNet = rows.devices.reduce(
    (p: any, c: any) =>
      p + (c["price_" + currency] || 0) * (c.quantity || 0),
    0
  );
  const servicesNet = rows.services.reduce(
    (p: any, c: any) =>
      p + (c["price_" + currency] || 0) * (c.quantity || 0),
    0
  );
  const totalNet = devicesNet + (quoteUserProperties.include_currency_visibility === 2 && quoteUserProperties.include_labour_cost === 0 ? 0 : servicesNet);

  // Group devices by manufacturer, sum prices. Other devices get __default__ as their manufacturer id/name
  rows.devices.forEach((device: any) => {
    const manufacturerName = device.manufacturer_name || "__default__";
    const manufacturerId = manufacturerNameToId
      ? manufacturerNameToId[manufacturerName]
      : "__default__";

    if (!(manufacturerId in pricesByManufacturers)) {
      pricesByManufacturers[manufacturerId] = {
        id: manufacturerId,
        name: manufacturerName,
        netPrice: 0,
      };
    }

    pricesByManufacturers[manufacturerId].netPrice +=
      (device["price_" + currency] || 0) *
      (device["quantity"] || 0);
  });

  rows.services.forEach((service: any) => {
    const serviceName = service.product_name.split("(#")[0] || "__default__";
    const serviceId = serviceNameToId
      ? serviceNameToId[serviceName]
      : "__default__";

    if (!(serviceId in pricesByServices)) {
      pricesByServices[serviceId] = {
        id: serviceId,
        name: serviceName,
        netPrice: 0,
      };
    }

    pricesByServices[serviceId].netPrice +=
      (service["price_" + currency] || 0) *
      (service["quantity"] || 0);

    pricesByServices[serviceId].discount = service.discount;
  });

  // Filter manufacturer discount rows
  // changed x[1].id to x[1].name cause i got undefined id
  const manufacturerDiscountRows = Object.entries(pricesByManufacturers)
    .filter((x: any) => x[1].name !== "__default__")
    .map((x: any) => ({
      id: x[1].id,
      name: x[1].name,
      originalPrice: x[1].netPrice,
      valuePcnt: discountPercentOfManufacturer(x[1].id),
      valueAbs: roundToNeededDecimals(
        (discountPercentOfManufacturer(x[1].id) / 100) * x[1].netPrice
      ),
    }))
    .filter((x) => x.originalPrice > 0.0001);

  const serviceDiscountRows = Object.entries(pricesByServices)
    .filter((x: any) => x[1].id !== "__default__")
    .map((x: any) => ({
      id: x[1].id,
      name: x[1].name,
      originalPrice: x[1].netPrice,
      valuePcnt: discountPercentOfService(x[1].id) || 0,
      valueAbs: roundToNeededDecimals(
        ((discountPercentOfService(x[1].id) || 0) / 100) * x[1].netPrice
      ),
    }))
    .filter((x) => x.originalPrice > 0.0001);

  // Sum ALL Manufacturer discounts, except "others"
  const manufacturerDiscountsAbs = manufacturerDiscountRows.reduce(
    (p, c) => p + c.valueAbs,
    0
  );

  const serviceDiscountsAbs = serviceDiscountRows.reduce(
    (p, c) => p + c.valueAbs,
    0
  );

  // Sum "other" device prices
  const otherDevicesNet = Object.entries(pricesByManufacturers)
    .filter((x: any) => x[1].id === "__default__")
    .reduce((p: any, c: any) => p + c[1].netPrice, 0);

  // calc other device, service discounts, and finally the total discount
  const deviceDiscountAbs = roundToNeededDecimals(
    (otherDevicesNet * otherDeviceDiscountPercent) / 100
  );

  const totalDiscount = (quoteUserProperties.include_discount === 1) ? Math.min(
    manufacturerDiscountsAbs + deviceDiscountAbs + serviceDiscountsAbs,
    totalNet
  ) : 0; // this cannot be higher than subtotal
  const totalDiscountedNet = Math.max(totalNet - totalDiscount, 0); // this cannot be lower than 0

  // calc tax and total price
  const taxAbs = roundToNeededDecimals(
    (totalDiscountedNet * quote["tax-value"]) / 100
  );
  const grossPrice = totalDiscountedNet + taxAbs;

  const hasEditPermission = hasPermissionForFuncInProject(
    "quote_sect.change_discount_n_tax",
    projectId
  );

  let needHeaderOnExport = false;

  manufacturerDiscountRows.map((row) => {
    if (row.valuePcnt > 0) {
      needHeaderOnExport = true;
    }
  });

  serviceDiscountRows.map((row) => {
    if (row.valuePcnt > 0) {
      needHeaderOnExport = true;
    }
  });

  return discountPercents === null || manufacturerNameToId === null ? (
    <div style={{ width: "100%", textAlign: "center" }}>
      <i
        className="fa fa-cog fa-spin"
        style={{ fontSize: 32, color: "var(--uplan-blue)" }}
      ></i>
    </div>
  ) : (
    <PriceContext.Provider
      value={{
        currency: currency,
        onEditHandler: onEditHandler.bind(this),
        onConfirmHandler: onConfirmHandler.bind(this),
        hasEditPermission,
      }}
    >
      <div className="quote-footer" style={{ marginBottom: 15 }}>
        {quoteUserProperties.include_discount === 1 &&
            quoteUserProperties.include_currency_visibility > 0 ? (
          <Fragment>
            {quoteUserProperties.include_currency_visibility > 1 ?(
            <PriceTable
              titleBeforeTable={ts("quote_overall_cost", "Overall Cost")}
            >
              <PriceRow
                name={ts("devices", "Devices")}
                valueAbs={devicesNet}
                quoteUserProperties={quoteUserProperties}
              />
              {quoteUserProperties.include_labour_cost === 1 ? (
                <PriceRow
                  name={ts("q_services", "Services")}
                  valueAbs={servicesNet}
                  quoteUserProperties={quoteUserProperties}
                />
              ) : null}
                <PriceRow
                highlighted={true}
                name={ts("q_subtotal", "Subtotal")}
                valueAbs={totalNet}
                />
            </PriceTable>
              ) : null}
            <PriceTable
              className="quote-footer-discount-table"
              titleBeforeTable={ts("quote_discounts", "Discounts")}
              needHeaderOnExport={needHeaderOnExport}
            >
              {hasEditPermission && (
                <PriceRow
                  sectionStart={true}
                  editKey={"deviceDiscount"}
                  name={ts("q_dev_disc", "Discount for devices")}
                  valuePcnt={deviceDiscountPercent}
                  confirmedEdit={true}
                  quoteUserProperties={quoteUserProperties}
                />
              )}

              {manufacturerDiscountRows.map((row) => (
                <PriceRow
                  editKey={"manu_" + row.id}
                  key={row.name}
                  name={ts2("q_manu_disc", "Discount for {manufacturer}", {
                    manufacturer: row.name,
                  })}
                  valuePcnt={row.valuePcnt}
                  valueAbs={-row.valueAbs}
                  quoteUserProperties={quoteUserProperties}
                />
              ))}

              {otherDevicesNet > 0.0001 && (
                <PriceRow
                  editKey={"otherDeviceDiscount"}
                  name={ts("q_dev_disc_other", "Discount for other devices")}
                  valuePcnt={otherDeviceDiscountPercent}
                  valueAbs={-deviceDiscountAbs}
                  quoteUserProperties={quoteUserProperties}
                />
              )}

              {hasEditPermission && (
                <PriceRow
                  sectionStart={true}
                  editKey={"serviceDiscount"}
                  name={ts("q_ser_disc", "Discount for services")}
                  valuePcnt={serviceDiscountPercent}
                  confirmedEdit={true}
                  quoteUserProperties={quoteUserProperties}
                />
              )}

              {serviceDiscountRows.map((row) => (
                <PriceRow
                  editKey={"service_" + row.id}
                  key={row.name}
                  name={ts2("q_manu_disc", "Discount for {manufacturer}", {
                    manufacturer: row.name,
                  })}
                  valuePcnt={row.valuePcnt}
                  valueAbs={-row.valueAbs}
                  quoteUserProperties={quoteUserProperties}
                />
              ))}
            </PriceTable>
          </Fragment>
        ) : null}
        {quoteUserProperties.include_currency_visibility >= 1 && quoteUserProperties.include_total_price === 1 ? (
          <div>
            <div>
              {quoteUserProperties.include_discount === 1 && quoteUserProperties.include_currency_visibility > 1 ? (
                <PriceTable className={"total-price"}>
                  {totalDiscount > 0.0001 && (
                    <PriceRow
                        highlighted={true}
                        name={ts("q_total_discount", "Total discount")}
                        valueAbs={-totalDiscount}
                        quoteUserProperties={quoteUserProperties}
                    />
                  )}
                </PriceTable>
              ) : null }
            </div>
            <div>
              <GrandTotal
                  newColumns={[
                    { data: "subtotal_discounted", title: ts("q_subtotal_discounted", "Discounted Net Total"), width: "30%" },
                    { data: "tax_rate", title: ts("q_tax_rate", "Tax rate"), width: "20%" },
                    { data: "tax", title: ts("q_tax", "Taxes"), width: "20%" },
                    { data: "total_price", title: ts("grand_total", "Grand total"), width: "30%" }
                  ]}
                  newRows={[
                    { rowKey: "subtotal_discounted", subtotal_discounted: totalDiscountedNet },
                    { rowKey: "tax_rate", tax_rate: quote["tax-value"] },
                    { rowKey: "tax", tax: taxAbs },
                    { rowKey: "total_price", total_price: grossPrice }
                  ]}
              />
              <PriceTable
                  className={"total-price-new"}
              >
                <PriceRow
                    highlighted={true}
                    highlightedText={true}
                    name={ts("grand_total", "Grand total")}
                    valueAbs={grossPrice}
                />
              </PriceTable>
              <div className="remove-on-export" style={{ margin: "25px", textAlign: "center"}}>
                <span
                  style={{
                    background: "var(--uplan-blue-lighter-4)",
                    color: "var(--uplan-blue-darker-4)",
                    padding: "10px 18px",
                  }}
                >
                  <i
                    className="fa fa-info-circle"
                    style={{
                      fontSize: 18,
                      position: "relative",
                      top: 1.5,
                      marginRight: 10,
                    }}
                  />
                  {ts("list_price_info", "We use official list prices in our quote")}
                </span>
              </div>
            </div>
          </div>
          ) : null}
      </div>
    </PriceContext.Provider>
  );
};
